import Cookies from 'js-cookie';
import { Auth } from '@aws-amplify/auth';
import axios from 'axios';
import { AmplifyClient } from 'project/amplify-client';
import { formConstants } from '../../../components/sso/components/utils/constants/form-constants';
import { isEmpty } from '../../../components/sso/components/utils/obj-utils';
import * as cookieHelper from '../../../components/sso/helpers/cookie-helper';
import {
    BALLOT_ORDER_FILE_NUMBER_COOKIE_NAME,
    TOCH_USER_ADDRESS_BOOK_LOCALSTORAGE_NAME,
    USER_STATE_VALID
} from './constants';
import * as userAccountApi from '../../../components/sso/store/api/user-account-api';
import { TICKETS_DATA } from '../../../components/banner/js/helpers/bannerHelperConstants';
import { domainConstants } from '../../../components/sso/components/utils/constants/domain-constants';
import Oauth from '../../../components/sso/components/oauth';
import { CookieNames } from '../utils/cookies';
import { hasValue } from '../general-helpers';
import eventBus from '../event-bus';
import { BusEvents } from '../event-bus-constants';
import { deleteFromSessionStorage } from '../utils';
import { ENDEAVOR_AUTH } from '../api/endeavor/constants/api-constants';

const ACCESS_TOKEN_COOKIE_NAME = window.appState.ssoConfig.cookieStorage.accessTokenCookieName;
const USER_DATA_COOKIE_NAME = window.appState.ssoConfig.cookieStorage.userDataCookieName;
const COOKIE_DOMAIN = window.appState.ssoConfig.cookieStorage.domain;
const CHECK_USER_STATE_INTERVAL = window.appState.ssoConfig.checkUserStateInterval || 0;

const api = axios.create({
    timeout: 60000,
    withCredentials: true,
});

class UserService {
    constructor(userProfileBaseUrl) {
        AmplifyClient.init();
        this.userProfileBaseUrl = `${userProfileBaseUrl}UserProfile`;
    }

    getCurrentUser() {
        return this.getAccessToken().then((token) => {
            return api.get(this.getUserProfileUrl(), {
                headers: { Authorization: `Bearer ${token}` }
            }).then((response) => {
                return Promise.resolve(response.data);
            });
        });
    }

    signOut() {
        this.setAccessTokenCookie(null);
        this.setUserDataCookie(null);
        this.removeDataCookie(BALLOT_ORDER_FILE_NUMBER_COOKIE_NAME);
        this.removeDataFromLocalStorage(TICKETS_DATA);
        this.removeDataFromLocalStorage(USER_STATE_VALID);
        deleteFromSessionStorage(ENDEAVOR_AUTH);

        const key = window.sifantasystorageKey;
        if (key) {
            this.removeDataFromLocalStorage(key);
        }

        return this.clearSecutixUserDataCookie().then(() => {
            Auth.signOut({ global: true }).then((result) => {
                this.clearTOCHData();
                Oauth.handleOauthSignedOut({});
                return result;
            }).catch((error) => {
                if (error.code === 'NotAuthorizedException') {
                    window.location.reload();
                    return;
                }
                throw error;
            });
        });
    }

    clearLoggedInData() {
        this.setAccessTokenCookie(null);
        this.setUserDataCookie(null);
        this.removeDataCookie(BALLOT_ORDER_FILE_NUMBER_COOKIE_NAME);
        this.removeDataFromLocalStorage(TICKETS_DATA);

        return this.clearSecutixUserDataCookie().then(() => {
            this.clearTOCHData();
        });
    }

    clearTOCHData() {
        if (cookieHelper.getCookie(CookieNames.TOCH_USER_DATA)) {
            this.removeDataCookie(CookieNames.TOCH_USER_DATA);
        }
        if (this.getDataFromLocalStorage(TOCH_USER_ADDRESS_BOOK_LOCALSTORAGE_NAME)) {
            this.removeDataFromLocalStorage(TOCH_USER_ADDRESS_BOOK_LOCALSTORAGE_NAME);
        }
        if (this.getDataFromLocalStorage(CookieNames.TOCH_USER_MEMBERSHIPS)) {
            this.removeDataFromLocalStorage(CookieNames.TOCH_USER_MEMBERSHIPS);
        }
        if (this.getDataFromLocalStorage(CookieNames.TOCH_USER_EMAIL)) {
            this.removeDataFromLocalStorage(CookieNames.TOCH_USER_EMAIL);
        }
    }

    setAccessTokenCookie(token) {
        this.setDataCookie(ACCESS_TOKEN_COOKIE_NAME, token);
    }

    setUserDataCookie(userData) {
        this.setDataCookie(USER_DATA_COOKIE_NAME, userData);
    }

    setTOCHUserMembershipsInLocalStorage(userMemberships) {
        this.setDataInLocalStorage(CookieNames.TOCH_USER_MEMBERSHIPS, userMemberships);
        eventBus.emit(BusEvents.USER_MEMBERSHIP_UPDATE, { bypassCache: true });
    }

    getDataFromLocalStorage(key) {
        return window.localStorage.getItem(key);
    }

    getJSONDataFromLocalStorage(key) {
        try {
            const dataString = this.getDataFromLocalStorage(key);

            if (!dataString) {
                return null;
            }
            const jsonData = JSON.parse(dataString);

            return jsonData;
        } catch {
            return null;
        }
    }

    setDataInLocalStorage(key, value) {
        window.localStorage.setItem(key, value);
    }

    removeDataFromLocalStorage(key) {
        window.localStorage.removeItem(key);
    }

    clearSecutixUserDataCookie() {
        return api.get('/api/sitecore/account/logout').then(() => {
            return true;
        });
    }

    async getUserAccessToken() {
        try {
            const currentSession = await Auth.currentSession();
            return currentSession.accessToken.jwtToken;
        } catch (error) {
            return null;
        }
    }

    //TODO: Remove after ballot UAT
    getAccessToken() {
        return Auth.currentSession().then((data) => {
            const token = data.accessToken.jwtToken;
            this.setAccessTokenCookie(token);
            return token;
        });
    }

    getUserProfileUrl() {
        return `${this.userProfileBaseUrl}`;
    }

    loadUserOrderHistory(callbackUrl, ssoDomain) {
        const cookieBallot = Cookies.get(BALLOT_ORDER_FILE_NUMBER_COOKIE_NAME);
        if (cookieBallot) {
            return null;
        }
        return Auth.currentSession().then((user) => {
            const token = user.accessToken.jwtToken;
            if (token) {
                userAccountApi.getUserOrderHistoryData(callbackUrl, {
                    pageNumber: 1,
                    displayCancelledFiles: false,
                    [formConstants.ssoDomain]: ssoDomain
                }).then((data) => {
                    let fileIds = '';
                    if (data.data && data.data.orders) {
                        data.data.orders.forEach((element) => {
                            if (element.status === formConstants.SecutixReservationStatus ||
                                (element.orderType === formConstants.SecutixReservationOrderType &&
                                    element.status === formConstants.SecutixReservationRunningStatus)) {
                                fileIds = fileIds + element.fileNumber + ',';
                            }
                        });
                    }

                    if (fileIds !== '') {
                        this.setDataCookie(BALLOT_ORDER_FILE_NUMBER_COOKIE_NAME, unescape(fileIds), 3600 * 24);
                    } else if (this.getDataCookie(BALLOT_ORDER_FILE_NUMBER_COOKIE_NAME)) {
                        this.setEmptyCookie(BALLOT_ORDER_FILE_NUMBER_COOKIE_NAME, 3600 * 24);
                    }
                }).catch((error) => {
                    this.setEmptyCookie(BALLOT_ORDER_FILE_NUMBER_COOKIE_NAME);
                    console.error('Error:', error);
                });
            }
        });
    }

    loadTicketsInfo(getTicketsCallbackUrl) {
        this.loadTicketsInfoByDomain(domainConstants.theOpen, getTicketsCallbackUrl).then(() =>
            this.loadTicketsInfoByDomain(domainConstants.womensOpen, getTicketsCallbackUrl));
    }

    async loadTicketsInfoByDomain(domain, getTicketsCallbackUrl) {
        let ticketsData = this.getJSONDataFromLocalStorage(TICKETS_DATA);
        if (ticketsData && ticketsData[domain] && ticketsData[domain].expiryDate > new Date().toJSON()) {
            return null;
        }
        return userAccountApi.getUserTicketsData(getTicketsCallbackUrl,
            true, domain).then(
            (response) => {
                const expiryDate = new Date(new Date().getTime() + (2 * 24 * 60 * 60 * 1000));
                ticketsData = response.data && response.data.eventTickets && response.data.eventTickets.length ?
                    { ...ticketsData, [domain]: { hasTickets: true, expiryDate } } :
                    { ...ticketsData, [domain]: { hasTickets: false, expiryDate } };
                this.setDataInLocalStorage(TICKETS_DATA, JSON.stringify(ticketsData));
            }
        )
            .catch((error) => {
                console.log(error);
            });
    }

    setDataCookie(userDataCookieName, cookieData, expires = 3600) {
        const options = { domain: COOKIE_DOMAIN, expires };
        let cookie = `${userDataCookieName}=${cookieData};path=/;`;

        if (cookieData) {
            Cookies.set(userDataCookieName, cookieData, options);
        } else {
            Cookies.remove(userDataCookieName, options);
            cookie = `${cookie}expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
        }

        document.cookie = cookie;
    }

    setEmptyCookie(userDataCookieName, expires = 3600) {
        const options = { domain: COOKIE_DOMAIN, expires };
        const cookie = `${userDataCookieName}=0;path=/;`;

        Cookies.set(userDataCookieName, '0', options);

        document.cookie = cookie;
    }

    getDataCookie(cookieName) {
        const cookie = `${cookieName}=${Cookies.get(cookieName)};path=/;`;
        return cookie;
    }

    removeDataCookie(cookieName) {
        const options = { domain: COOKIE_DOMAIN, expires: 3600 };
        let cookie = this.getDataCookie(cookieName);
        Cookies.remove(cookieName, options);
        cookie = `${cookie}expires=Thu, 01 Jan 1970 00:00:00 GMT;`;

        document.cookie = cookie;
    }

    setHostedUIReturnUrl(returnUrl, isForSignIn = false) {
        const sessionItemName = this.getSessionItemNameForReturnUrl(isForSignIn);

        sessionStorage.setItem(sessionItemName, returnUrl);
    }

    getHostedUIReturnUrlFromSessionStorage(isForSignIn = false) {
        const sessionItemName = this.getSessionItemNameForReturnUrl(isForSignIn);

        const returnUrl = sessionStorage.getItem(sessionItemName);

        if (isEmpty(returnUrl)) {
            return null;
        }

        //Clear SessionStorage
        sessionStorage.removeItem(sessionItemName);

        return decodeURIComponent(returnUrl);
    }

    getSessionItemNameForReturnUrl(isForSignIn) {
        return isForSignIn ?
            formConstants.hostedUISignInReturnUrlSessionItemName :
            formConstants.hostedUISignOutReturnUrlSessionItemName;
    }

    onSignInLinkClickAction(returnUrl, isForSignIn) {
        this.setHostedUIReturnUrl(returnUrl, isForSignIn);
        Auth.federatedSignIn();
    }

    hasTicketingAccount(userData) {
        return hasValue(userData.ticketingAccountId);
    }

    shouldCheckAuthUser() {
        try {
            const userCheckedDate = this.getDataFromLocalStorage(USER_STATE_VALID);
            return !userCheckedDate || new Date(userCheckedDate) < new Date();
        } catch (error) {
            return true;
        }
    }

    setCheckedUser() {
        const date = new Date();
        date.setSeconds(date.getSeconds() + CHECK_USER_STATE_INTERVAL);
        this.setDataInLocalStorage(USER_STATE_VALID, date);
    }
}

const instance = new UserService(window.appState.ssoConfig.userProfileApiUrl);
export default instance;
